import IconButton from '@/storybook/components/IconButton/IconButton';
import Text from '@/storybook/components/Text/Text';
import { CalendarIcon } from '@/storybook/icons';
import CircleInfoIcon from '@/storybook/icons/CircleInfoIcon';
import cx from 'classnames';
import { forwardRef, RefObject } from 'react';
import styles from './DateRangeInput.module.css';

const FULL_PLACEHOLDER = 'DD MMM YYYY - DD MMM YYYY';

interface DateRangeTextInputProps {
  name: string;
  label: string;
  inputValue: string;
  errorMessageText: string;
  calendarButtonRef: RefObject<HTMLButtonElement | null>;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  onBlur: () => void;
  onCalendarClick: () => void;
}

const DateRangeTextInput = forwardRef<HTMLInputElement, DateRangeTextInputProps>(
  (
    { name, label, inputValue, errorMessageText, calendarButtonRef, onChange, onPaste, onBlur, onCalendarClick },
    ref,
  ) => {
    const dynamicPlaceholder = inputValue.length > 0 ? FULL_PLACEHOLDER.slice(inputValue.length) : FULL_PLACEHOLDER;

    return (
      <div className={styles.inputWrapper}>
        <Text className={styles.label}>{label}</Text>
        <div className={cx(styles.inputContainer, errorMessageText && styles.hasError)}>
          <div className={styles.inputWithOverlay}>
            <input
              ref={ref}
              type="text"
              value={inputValue}
              onChange={onChange}
              onPaste={onPaste}
              onBlur={onBlur}
              placeholder=""
              className={styles.input}
              data-testid={`${name}-input`}
            />
            {dynamicPlaceholder && (
              <div className={styles.placeholderOverlay}>
                <span className={styles.userText}>{inputValue}</span>
                {dynamicPlaceholder}
              </div>
            )}
          </div>
          <IconButton
            appearance="text"
            size="M"
            ref={calendarButtonRef}
            className={styles.iconButton}
            onClick={onCalendarClick}
            data-testid={`${name}-calendar-button`}
            icon={<CalendarIcon className={styles.calendarIcon} />}
          />
        </div>
        {errorMessageText && (
          <div className={styles.validation}>
            <CircleInfoIcon />
            <Text className={styles.errorMessage}>{errorMessageText}</Text>
          </div>
        )}
      </div>
    );
  },
);

DateRangeTextInput.displayName = 'DateRangeTextInput';

export default DateRangeTextInput;
